import { computeGstAmountForUnit } from '../gst';
import { buildPaymentRoadmap } from '../paymentCondition';
import { buildingBucReceived, fullPaymentReceived } from '../deedPossessionGates';
import { activeBookingCustomerReceipts, parseDateLike, resolveTxnDateLike, toMs, type BankTransactionRow } from '../today/collections';
import { toYmd } from '../today/dues';
import { allStepsForUnit, completedKeysOf, sanctionOf, type UnitStep, type WorkflowCase, type WorkflowDefinitionStep } from '../today/workflowBoard';
import { buildLifecycle, type LifecycleStage } from './lifecycle';
import { changeOrderRows } from './unitExtras';
import { buildNetReceiptRows } from '../paymentPlaceholders';
import type { ChangeOrderDoc, CustomerDoc, UnitDoc } from './useUnitPageData';

export type PlanRow = { label: string; dueMs: number | null; dueLabel: string; amount: number; paid: number; state: 'paid' | 'part' | 'late' | 'due' | 'later' };
export type ReceiptRow = { id: string; ms: number; label: string; kind: 'Customer' | 'Loan' | 'GST' | 'Legal' | 'Other' | 'Refund'; mode: string; reference: string; amount: number; refundedAmount: number; voucherUrl: string | null };
export type Attention = { id: string; tone: 'bad' | 'warn'; title: string; detail: string };
export type Buyer = { id: string; name: string; role: string; mobile: string; kycCount: number; primary: boolean };

const DAY = 86400000;
const ceilTo100 = (v: number) => Math.ceil(v / 100) * 100;
const nameOf = (c: any) =>
  String(c?.displayName || c?.fullName || c?.customerName || c?.allotteeName || c?.name || [c?.firstName, c?.middleName, c?.surname || c?.lastName].filter(Boolean).join(' ')).trim();
const isPrimary = (c: any) => c?.isPrimary === true || String(c?.role || c?.customerType || '').toLowerCase().includes('primary');

// Same field lookup as the Reports page: top-level first, then any step's saved data.
export const fieldFromBooking = (booking: any, keys: string[]): any => {
  if (!booking || typeof booking !== 'object') return null;
  for (const k of keys) if (booking[k]) return booking[k];
  const stepData = booking.stepData;
  if (stepData && typeof stepData === 'object') {
    for (const entry of Object.values(stepData as Record<string, any>)) {
      if (!entry || typeof entry !== 'object') continue;
      for (const k of keys) if ((entry as any)[k]) return (entry as any)[k];
    }
  }
  return null;
};

/** Legal charges a unit owes, using the same formulas as the booking drawer. */
export function legalCharges(unit: UnitDoc, customers: Array<Record<string, any>>, projectMeta: Record<string, any> | null) {
  const value = Math.max(0, Number(unit.unitAmount || 0));
  const hasMale = customers.some((c) => String(c?.gender || '').trim().toLowerCase() === 'male');
  const stamp = Number(unit.stampDuty ?? unit.stamp_duty ?? 0) || (value > 0 ? ceilTo100(ceilTo100(value) * 0.049) : 0);
  const registration = Number(unit.registrationCharges ?? unit.registration_charges ?? 0) ||
    (value <= 0 ? 0 : customers.length === 0 || hasMale ? ceilTo100(ceilTo100(value) * 0.01) + 1200 : 1200);
  return {
    stamp_duty: stamp,
    registration_charges: registration,
    agreement_fee: Number(projectMeta?.advocateAgreementFee || 0) || 0,
    deed_fee: Number(projectMeta?.advocateDeedFee || 0) || 0,
  } as Record<string, number>;
}

export function buildUnitSummary(args: {
  unit: UnitDoc;
  booking: Record<string, any> | null;
  bookingHistoryCount: number;
  customers: CustomerDoc[];
  txns: BankTransactionRow[];
  projectMeta: Record<string, any> | null;
  mainCase: WorkflowCase | null;
  loanCase: WorkflowCase | null;
  mainSteps: WorkflowDefinitionStep[];
  loanSteps: WorkflowDefinitionStep[];
  changeOrders?: ChangeOrderDoc[];
  now?: Date;
}) {
  const { unit, booking, customers, txns, projectMeta, mainCase, loanCase, mainSteps, loanSteps } = args;
  const now = args.now || new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const unitId = unit.id;
  const bookingId = String(unit.activeBookingId || '').trim();
  const value = Math.max(0, Number(unit.unitAmount || 0));
  const units = [{ id: unitId, activeBookingId: bookingId }];
  const meta = new Map([[unitId, { bookingCreatedAtMs: toMs(booking?.createdAt), bookingHistoryCount: args.bookingHistoryCount }]]);

  /* money in */
  const customerRows = activeBookingCustomerReceipts(txns, units, meta);
  const received = customerRows.reduce((s, r) => s + r.amount, 0);
  const loanDisbursed = customerRows.filter((r) => (r.txn as any).isLoanDisbursement === true && r.amount > 0).reduce((s, r) => s + r.amount, 0);
  const gstPaid = activeBookingCustomerReceipts(txns, units, meta, 'gst').reduce((s, r) => s + r.amount, 0);
  const explicitGst = Number(unit.gst ?? unit.gstAmount ?? unit.gst_amount ?? NaN);
  const gstDue = Number.isFinite(explicitGst) && explicitGst > 0 ? explicitGst : computeGstAmountForUnit(value, unit as any, (projectMeta as any) || null, 1);

  // Legal (advocate) receipts split by charge type, for this booking only.
  const legalPaid: Record<string, number> = {};
  txns.forEach((t: any) => {
    if (String(t.txnType || '') !== 'receipt' || String(t.receiptCategory || '') !== 'advocate') return;
    if (bookingId && t.bookingId && String(t.bookingId) !== bookingId) return;
    if (Array.isArray(t.legalChargeAllocations)) {
      t.legalChargeAllocations.forEach((a: any) => { const k = String(a?.type || ''); if (k) legalPaid[k] = (legalPaid[k] || 0) + (Number(a?.amount) || 0); });
    } else if (t.legalChargeType) legalPaid[t.legalChargeType] = (legalPaid[t.legalChargeType] || 0) + (Number(t.amount) || 0);
  });
  const legalDue = legalCharges(unit, customers, projectMeta);

  /* payment plan, with what is paid applied to the oldest demand first */
  const bookedOn = parseDateLike(booking?.bookingFormDate) || parseDateLike(booking?.createdAt);
  const plan: PlanRow[] = [];
  const condition = booking?.paymentCondition;
  if (bookedOn && condition && Array.isArray(condition.stages) && condition.stages.length && value > 0) {
    const fromYmd = (v?: string) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || '')); return m ? new Date(+m[1], +m[2] - 1, +m[3]).getTime() : null; };
    buildPaymentRoadmap(condition, toYmd(bookedOn)).forEach((stage) => {
      if (stage.instalments?.length) stage.instalments.forEach((r) => plan.push({ label: `${stage.label}, ${r.label}`, dueMs: fromYmd(r.dueDate), dueLabel: r.due, amount: Math.round((value * r.percent) / 100), paid: 0, state: 'due' }));
      else if (stage.percent > 0) plan.push({ label: stage.label, dueMs: fromYmd(stage.dueDate), dueLabel: stage.due, amount: Math.round((value * stage.percent) / 100), paid: 0, state: 'due' });
    });
    let left = Math.max(0, received);
    plan.forEach((r) => {
      r.paid = Math.min(left, r.amount);
      left -= r.paid;
      r.state = r.paid >= r.amount ? 'paid' : r.dueMs == null ? 'later' : r.dueMs < today ? 'late' : r.paid > 0 ? 'part' : 'due';
    });
  }
  const lateRows = plan.filter((r) => r.state === 'late');
  const overdue = lateRows.reduce((s, r) => s + (r.amount - r.paid), 0);
  const nextDue = plan.find((r) => r.state !== 'paid') || null;

  /* paperwork */
  const agreementDate = parseDateLike(fieldFromBooking(booking, ['sale_agreement_date', 'saleAgreementDate', 'agreement_date', 'agreementDate']) || unit.saleAgreementDate);
  const deedDate = parseDateLike(fieldFromBooking(booking, ['sale_deed_date', 'saleDeedDate']));
  const deedDone = !!(deedDate || fieldFromBooking(booking, ['sale_deed_number', 'saleDeedNumber']));

  /* loan */
  // A loan counts when the workflow says so, a loan-to-builder amount is set, or loan money has already come in.
  const loanNeeded = String(mainCase?.loanRequired || '').toLowerCase() === 'yes' || Number(unit.loanToDeveloperAmount || 0) > 0 || loanDisbursed > 0;
  const sanction = sanctionOf(loanCase, loanSteps);
  const sanctionStep = loanSteps.find((s) => String(s.actionType || '').toLowerCase() === 'loan_sanction_form' && (loanCase?.stepData || {})[s.key]?.sanctionAmount);
  const sanctionData = sanctionStep ? (loanCase?.stepData || {})[sanctionStep.key] : null;
  const loanAmount = Number(sanctionData?.sanctionAmount || 0) || Math.max(0, Number(unit.loanToDeveloperAmount || 0));

  /* buyers */
  const buyers: Buyer[] = customers
    .map((c) => ({ id: c.id, name: nameOf(c), role: isPrimary(c) ? 'Primary' : String(c.role || 'Co-applicant'), mobile: String(c.mobile || c.mobileNo || c.phone || '').trim(), kycCount: c.kycCount, primary: isPrimary(c) }))
    .sort((a, b) => Number(b.primary) - Number(a.primary));

  /* receipts list: everything recorded against this booking, newest first */
  const bookingTransactions = txns.filter((t: any) => !bookingId || !t.bookingId || String(t.bookingId) === bookingId);
  const netReceiptAmountById = new Map(
    buildNetReceiptRows(bookingTransactions).map((row: any) => [String(row.id || ''), Number(row.amount || 0)])
  );
  const receipts: ReceiptRow[] = bookingTransactions
    .filter((t: any) => Number(t.amount || 0) > 0)
    .filter((t: any) => String(t.txnType) === 'receipt' || (String(t.txnType) === 'payment' && String(t.paymentCategory) === 'customer_refund'))
    .map((t: any) => {
      const refund = String(t.txnType) === 'payment' && String(t.paymentCategory) === 'customer_refund';
      const cat = String(t.receiptCategory || '');
      const kind: ReceiptRow['kind'] = refund ? 'Refund' : t.isLoanDisbursement ? 'Loan' : cat === 'gst' ? 'GST' : cat === 'advocate' ? 'Legal' : cat === 'customer' ? 'Customer' : 'Other';
      return {
        id: t.id,
        ms: resolveTxnDateLike(t)?.getTime() || 0,
        label: refund ? 'Refund to buyer' : kind === 'Legal' ? (Array.isArray(t.legalChargeAllocations) ? t.legalChargeAllocations.map((a: any) => String(a.type).replace(/_/g, ' ')).join(', ') : 'Legal charges') : kind === 'Loan' ? 'Loan disbursal' : kind === 'GST' ? 'GST' : 'Payment',
        kind,
        mode: String(t.mode || ''),
        reference: String(t.referenceNo || t.payerBankName || ''),
        amount: Number(t.amount || 0),
        refundedAmount: refund ? 0 : Math.max(0, Number(t.amount || 0) - (netReceiptAmountById.get(String(t.id || '')) || 0)),
        voucherUrl: t.voucherUrl || null,
      };
    })
    .sort((a, b) => b.ms - a.ms);

  /* workflow and lifecycle */
  const fullPayment = fullPaymentReceived(value, received);
  const steps: UnitStep[] | null = mainSteps.length
    ? allStepsForUnit({ mainCase, loanCase, mainSteps, loanSteps, fullPayment, bucReceived: buildingBucReceived(projectMeta, unit), registeredDeed: deedDone })
    : null;
  const lifecycle: LifecycleStage[] = steps ? buildLifecycle(steps, { loanNeeded, fullyPaid: fullPayment, deedDone }) : [];
  const nextStep = steps?.find((s) => s.status === 'ready' && s.workflowType === 'main') || steps?.find((s) => s.status === 'ready') || null;

  /* needs attention */
  const attention: Attention[] = [];
  if (overdue > 0) attention.push({ id: 'overdue', tone: 'bad', title: `₹${Math.round(overdue).toLocaleString('en-IN')} overdue`, detail: `${lateRows.length} instalment${lateRows.length === 1 ? '' : 's'} unpaid, oldest due ${lateRows[0].dueLabel.replace(/^By /, '')}` });
  const paidPct = value > 0 ? (received / value) * 100 : 0;
  if (!agreementDate && paidPct > 10) attention.push({ id: 'rera10', tone: 'bad', title: `${Math.round(paidPct)}% received without a registered agreement`, detail: 'RERA Section 13 allows at most 10% of the price before the agreement for sale is registered.' });
  if (loanNeeded && sanction.sanctioned && agreementDate && loanDisbursed === 0) attention.push({ id: 'loan', tone: 'warn', title: 'Loan sanctioned but not disbursed', detail: `${sanction.bank || 'Bank'} has not released any amount yet.` });
  if (loanNeeded && !loanCase) attention.push({ id: 'loanfile', tone: 'warn', title: 'Loan needed, no loan file started', detail: 'Start the loan workflow so the bank steps are tracked.' });
  if (gstDue - gstPaid > 0 && received > 0) attention.push({ id: 'gst', tone: 'warn', title: `GST ₹${Math.round(gstDue - gstPaid).toLocaleString('en-IN')} pending`, detail: `₹${Math.round(gstPaid).toLocaleString('en-IN')} of ₹${Math.round(gstDue).toLocaleString('en-IN')} collected.` });
  const noKyc = buyers.filter((b) => b.kycCount === 0);
  if (bookingId && (!buyers.length || noKyc.length)) attention.push({ id: 'kyc', tone: 'warn', title: buyers.length ? `KYC missing for ${noKyc.length} buyer${noKyc.length === 1 ? '' : 's'}` : 'No buyer added yet', detail: buyers.length ? noKyc.map((b) => b.name).join(', ') : 'Add the buyer and their KYC documents.' });
  const changeOrders = changeOrderRows(args.changeOrders || [], now);
  const coWaiting = changeOrders.filter((c) => c.waitingBuyer && c.waitingDays > 7);
  if (coWaiting.length) attention.push({ id: 'co', tone: 'warn', title: `${coWaiting.length} change order${coWaiting.length === 1 ? '' : 's'} waiting on the buyer`, detail: coWaiting.map((c) => `${c.coNo} ${c.title} (${c.waitingDays} days)`).join(', ') });
  if (bookingId && !mainCase) attention.push({ id: 'workflow', tone: 'warn', title: 'Workflow not started', detail: 'No office steps are being tracked for this booking.' });

  const firstReceiptMs = receipts.filter((r) => r.kind === 'Customer' && r.ms > 0).reduce((min, r) => (min && min < r.ms ? min : r.ms), 0) || null;
  const lastReceipt = receipts.find((r) => r.kind === 'Customer' || r.kind === 'Loan') || null;

  return {
    value,
    received,
    pending: Math.max(0, value - received),
    // Same split as the booking drawer's Collection snapshot: loan to builder vs the buyer's own share (DP).
    collection: (() => {
      const loanAmount = Math.min(value, Math.max(0, Number(unit.loanToDeveloperAmount || 0) || 0));
      const dpAmount = Math.max(0, value - loanAmount);
      const loanIn = Math.max(0, loanDisbursed);
      const dpIn = Math.max(0, received - loanIn);
      const row = (label: string, amount: number, got: number) => ({ label, amount, received: got, pending: Math.max(0, amount - got) });
      return { total: row('Total', value, received), parts: loanAmount > 0 ? [row('Customer DP', dpAmount, dpIn), row('Loan to Builder', loanAmount, loanIn)] : [] };
    })(),
    paidPct: value > 0 ? Math.min(100, Math.round(paidPct)) : 0,
    overdue,
    overdueSinceLabel: lateRows[0]?.dueLabel.replace(/^By /, '') || '',
    nextDue,
    lastReceipt,
    plan,
    hasPlan: plan.length > 0,
    gst: { due: gstDue, paid: gstPaid },
    legal: Object.fromEntries(Object.keys(legalDue).map((k) => [k, { due: legalDue[k], paid: legalPaid[k] || 0 }])) as Record<string, { due: number; paid: number }>,
    loan: { needed: loanNeeded, asked: String(mainCase?.loanRequired || '').toLowerCase(), hasWorkflow: !!mainCase, started: !!loanCase, sanctioned: sanction.sanctioned, bank: sanction.bank || String(loanCase?.preferredBankName || ''), amount: loanAmount, sanctionDate: (() => { const d = parseDateLike(sanctionData?.sanctionDate); return d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : String(sanctionData?.sanctionDate || ''); })(), disbursed: loanDisbursed, doneSteps: completedKeysOf(loanCase).length },
    buyers,
    broker: String(booking?.refByBrokerName || '').trim(),
    scheme: booking?.schemeEnabled ? String(booking?.schemeRemark || 'Yes').trim() : '',
    // Booked = date of the first customer receipt; the booking form date is kept separately (due dates still count from it).
    dates: { booking: firstReceiptMs ? new Date(firstReceiptMs) : null, form: bookedOn, agreement: agreementDate, deed: deedDate },
    deedDone,
    receipts,
    steps,
    lifecycle,
    nextStep,
    attention,
    changeOrders,
    sanctionMs: parseDateLike(sanctionData?.sanctionDate)?.getTime() || null,
    daysSinceBooking: firstReceiptMs ? Math.floor((today - firstReceiptMs) / DAY) : 0,
  };
}

export type UnitSummary = ReturnType<typeof buildUnitSummary>;
